import { AttributionChips, RiskBadge, AddressDisplay, Tag, AddressAvatar } from './chunk-QGMTHC7I.js';
import { useChainRegistryVersion, ChainAvatar } from './chunk-UHTGLWOP.js';
import { Field } from './chunk-B25HJXFB.js';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from './chunk-TNAHBB5Q.js';
import { Label } from './chunk-676CI6I4.js';
import { Input } from './chunk-NHXI3UAE.js';
import { Badge } from './chunk-2WDEGOCV.js';
import { Button } from './chunk-PLP233UC.js';
import { cn } from './chunk-FFX3LZW7.js';
import { fieldLabel, chainText, displayChainText } from '@authe/fe-commons/display';
import { defaultTokenColor } from '@authe/fe-commons/theme';
import * as React4 from 'react';
import { jsx, jsxs } from 'react/jsx-runtime';
import { shortenAddress, getChainMetadata } from '@authe/fe-commons/chains';
import { RiskLevel } from '@authe/fe-commons/precedence';

var GRAPH_NODE_VARIANTS = [
  "address",
  "entity",
  "cluster",
  "contract",
  "tx",
  // Off-chain / special-infrastructure nodes (forensics): data feeds, cross-chain
  // bridges, decentralised oracle networks, and mixers/tumblers.
  "oracle",
  "bridge",
  "don",
  "mixer"
];
var GRAPH_EDGE_VARIANTS = [
  "transfer",
  "approval",
  "call",
  "inheritance",
  "flow",
  "bridge",
  "failed",
  // An analyst's own assertion (scene `origin: "user"`), not something the
  // decode observed. Its own variant rather than a modifier on the kind colours,
  // because "who says so" is the first thing a reader of someone else's case
  // file needs to know — and because the derived legend keys off variants, so
  // this earns its own row automatically wherever such an edge is on screen.
  "authored",
  "default"
];
var GRAPH_NODE_VARIANT_LABELS = {
  address: fieldLabel("address"),
  entity: fieldLabel("entity"),
  cluster: "Cluster",
  contract: "Contract",
  tx: "Transaction",
  oracle: "Oracle",
  bridge: "Bridge",
  don: "Oracle network",
  mixer: "Mixer"
};
var GRAPH_EDGE_VARIANT_LABELS = {
  transfer: "Transfer",
  approval: "Approval / allowance",
  call: "Contract call",
  inheritance: "Inheritance",
  flow: "Fund flow",
  bridge: "Bridge transfer",
  failed: "Failed / reverted",
  authored: "Analyst-added",
  default: "Link"
};
var GRAPH_NODE_VARIANT_TOKEN = {
  address: "--brand",
  entity: "--finality-confirmed",
  cluster: "--risk-medium",
  contract: "--finality-finalized",
  tx: "--muted-foreground",
  oracle: "--finality-pending",
  bridge: "--deploy-blue",
  don: "--deploy-green",
  mixer: "--risk-critical"
};
var GRAPH_EDGE_VARIANT_TOKEN = {
  transfer: "--brand",
  approval: "--muted-foreground",
  call: "--finality-confirmed",
  inheritance: "--finality-finalized",
  flow: "--risk-medium",
  bridge: "--deploy-blue",
  // Failed / reverted calls render in the critical-risk red so they read as errors.
  failed: "--risk-critical",
  // Deliberately not any of the observation colours: an analyst's assertion must
  // not be mistakable for a decoded flow at a glance.
  authored: "--risk-medium",
  default: "--muted-foreground"
};
function mapToCssVar(tokens) {
  return Object.fromEntries(
    Object.entries(tokens).map(([variant, cssVar]) => [variant, `hsl(var(${cssVar}))`])
  );
}
var GRAPH_NODE_VARIANT_COLOR = mapToCssVar(GRAPH_NODE_VARIANT_TOKEN);
var GRAPH_EDGE_VARIANT_COLOR = mapToCssVar(GRAPH_EDGE_VARIANT_TOKEN);
function resolveTokenColor(cssVar, fallback) {
  const fallbackColor = fallback ?? defaultTokenColor(cssVar) ?? "#888";
  if (typeof document === "undefined") return fallbackColor;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
  return raw ? `hsl(${raw})` : fallbackColor;
}
function graphNodeColor(variant, resolved = false) {
  return resolved ? resolveTokenColor(GRAPH_NODE_VARIANT_TOKEN[variant]) : GRAPH_NODE_VARIANT_COLOR[variant];
}
function graphEdgeColor(variant, resolved = false) {
  return resolved ? resolveTokenColor(GRAPH_EDGE_VARIANT_TOKEN[variant]) : GRAPH_EDGE_VARIANT_COLOR[variant];
}
var GRAPH_DEFAULT_HEIGHT = 480;
var GraphFrameContext = React4.createContext(false);
function GraphFrame({
  className,
  style,
  boxRef,
  ariaLabel,
  children
}) {
  return /* @__PURE__ */ jsx("div", { ref: boxRef, role: ariaLabel ? "region" : void 0, "aria-label": ariaLabel, tabIndex: ariaLabel ? -1 : void 0, className: cn("relative", className), style, children: /* @__PURE__ */ jsx("div", { className: "absolute inset-0", children: /* @__PURE__ */ jsx(GraphFrameContext.Provider, { value: true, children }) }) });
}
function GraphFrameMessage({
  className,
  children
}) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex size-full items-center justify-center p-8", className), children });
}
function useInGraphFrame() {
  return React4.useContext(GraphFrameContext);
}
function useGraphHeight(height, fallback = GRAPH_DEFAULT_HEIGHT) {
  const filling = useInGraphFrame();
  return height ?? (filling ? "100%" : fallback);
}
function TruncatedText({
  text,
  className,
  as = "span"
}) {
  const Trigger = as;
  const shown = displayChainText(text);
  return /* @__PURE__ */ jsxs(Tooltip, { children: [
    /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Trigger, { className, children: shown.text }) }),
    /* @__PURE__ */ jsx(TooltipContent, { className: "max-w-xs break-words", children: shown.title })
  ] });
}
function NodeIcon({
  src,
  alt,
  name,
  color,
  entityName
}) {
  if (!src && !color && !entityName) return null;
  const avatar = /* @__PURE__ */ jsx("span", { className: "inline-flex", children: /* @__PURE__ */ jsx(AddressAvatar, { logoUrl: src, color, entityName, nativeTitle: false, size: "sm" }) });
  const hover = (alt ?? entityName ?? name ?? "").trim();
  if (!hover) return avatar;
  return /* @__PURE__ */ jsxs(Tooltip, { children: [
    /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: avatar }),
    /* @__PURE__ */ jsx(TooltipContent, { className: "max-w-xs break-words", children: displayChainText(hover).title })
  ] });
}
function NodeChain({ chain }) {
  useChainRegistryVersion();
  return /* @__PURE__ */ jsxs(Tooltip, { children: [
    /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx("span", { className: "inline-flex", children: /* @__PURE__ */ jsx(ChainAvatar, { chain, size: "sm", showName: "sr-only", nativeTitle: false }) }) }),
    /* @__PURE__ */ jsx(TooltipContent, { children: getChainMetadata(chain)?.name ?? chain })
  ] });
}
function TagGlyph() {
  return /* @__PURE__ */ jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", className: "size-3", "aria-hidden": true, children: [
    /* @__PURE__ */ jsx("path", { d: "M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.4 2.4 0 0 0 3.42 0l6.58-6.58a2.4 2.4 0 0 0 0-3.42z" }),
    /* @__PURE__ */ jsx("circle", { cx: "7.5", cy: "7.5", r: "1.25", fill: "currentColor", stroke: "none" })
  ] });
}
var RISK_ACCENT = {
  [RiskLevel.HIGH]: "border-risk-high/70",
  [RiskLevel.SEVERE]: "border-risk-critical/70 ring-1 ring-risk-critical/30"
};
var VARIANT_STYLES = {
  address: "border-brand/40 bg-card",
  entity: "border-finality-confirmed/40 bg-card",
  cluster: "border-risk-medium/40 bg-card",
  contract: "border-finality-finalized/40 bg-card",
  tx: "border-muted bg-card",
  oracle: "border-finality-pending/50 bg-card",
  bridge: "border-deploy-blue/50 bg-card",
  don: "border-deploy-green/50 bg-card",
  mixer: "border-risk-critical/50 bg-card"
};
function slugAsWords(badge) {
  return badge && /^[a-z0-9]+(?:_[a-z0-9]+)+$/.test(badge) ? badge.replace(/_/g, " ") : badge;
}
function GraphNode({
  label,
  subtitle,
  chain,
  icon,
  iconAlt,
  avatarColor,
  avatarEntity,
  badge,
  contested,
  impersonates,
  variant = "address",
  accentClassName,
  riskLevel,
  riskScore,
  tags,
  attribution,
  chipPrefs,
  value,
  confidence,
  confidenceLabel = "Confidence",
  selected,
  dimmed,
  highlighted,
  failed,
  virtual,
  onAssign,
  className
}) {
  const hasFooter = riskLevel != null || value != null || confidence != null;
  const shownBadge = React4.useMemo(() => chainText(slugAsWords(badge), { maxChars: 24 }), [badge]);
  const chipBundle = React4.useMemo(() => {
    if (attribution) return attribution;
    if (!tags?.length) return null;
    return { tags: tags.map((t, i) => ({ id: `${t}-${i}`, name: t })) };
  }, [attribution, tags]);
  return /* @__PURE__ */ jsx(TooltipProvider, { delayDuration: 250, children: /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "min-w-[180px] max-w-[260px] rounded-md border p-2 shadow-xs transition-opacity",
        VARIANT_STYLES[variant],
        // A brand accent (e.g. per-protocol off-chain node) overrides the
        // variant border colour; the variant background is kept.
        accentClassName,
        // Risk outranks both. A brand accent says which protocol a node
        // belongs to; a high band says somebody flagged the address, and of
        // the two that is the one a reader must not be able to miss. `failed`
        // still wins below — a reverted call is a fact about THIS transaction,
        // where the band is a fact about the address in general.
        riskLevel != null && RISK_ACCENT[riskLevel],
        // A synthesized expected (virtual) step: dashed + ghosted so it reads
        // as inferred, not observed on chain. `failed` still wins below.
        virtual && "border-dashed opacity-60",
        // Failed/reverted nodes read as errors regardless of their base variant.
        failed && "border-risk-critical/70 ring-1 ring-risk-critical/40",
        selected && "ring-2 ring-brand",
        highlighted && !selected && "ring-2 ring-brand/60",
        dimmed && "opacity-30",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2", children: [
          /* @__PURE__ */ jsxs("span", { className: "flex min-w-0 items-center gap-1.5", children: [
            /* @__PURE__ */ jsx(NodeIcon, { src: icon, alt: iconAlt, name: label, color: avatarColor, entityName: avatarEntity }),
            /* @__PURE__ */ jsx(TruncatedText, { text: label, className: "truncate text-sm font-semibold" })
          ] }),
          /* @__PURE__ */ jsxs("span", { className: "flex shrink-0 items-center gap-1", children: [
            chain && /* @__PURE__ */ jsx(NodeChain, { chain }),
            onAssign && /* @__PURE__ */ jsxs(Tooltip, { children: [
              /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  "aria-label": "Label, tag, or assign entity",
                  className: "nodrag flex size-5 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
                  onMouseDown: (e) => e.stopPropagation(),
                  onClick: onAssign,
                  children: /* @__PURE__ */ jsx(TagGlyph, {})
                }
              ) }),
              /* @__PURE__ */ jsx(TooltipContent, { children: "Label, tag, or assign entity" })
            ] })
          ] })
        ] }),
        subtitle && /* @__PURE__ */ jsx(TruncatedText, { as: "p", text: subtitle, className: "mt-0.5 truncate text-xs text-muted-foreground" }),
        (badge || contested || impersonates) && /* @__PURE__ */ jsxs("div", { className: "mt-2 flex flex-wrap items-center gap-1", children: [
          badge && /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "max-w-full truncate", title: shownBadge.title, children: shownBadge.text }),
          contested && /* @__PURE__ */ jsx(
            Badge,
            {
              variant: "outline",
              className: "border-warning/60 text-warning",
              title: "Two analysts published different readings at the same tier",
              children: "Disputed"
            }
          ),
          impersonates && /* @__PURE__ */ jsxs(
            Badge,
            {
              variant: "outline",
              className: "max-w-full truncate border-risk-high/60 text-risk-high",
              title: `Address poisoning: a lookalike of ${impersonates}`,
              children: [
                "Lookalike of ",
                shortenAddress(impersonates)
              ]
            }
          )
        ] }),
        chipBundle && /* @__PURE__ */ jsx("div", { className: "mt-2", children: /* @__PURE__ */ jsx(AttributionChips, { bundle: chipBundle, max: 3, userPrefs: chipPrefs }) }),
        hasFooter && /* @__PURE__ */ jsxs("div", { className: "mt-2 flex items-center justify-between gap-2", children: [
          /* @__PURE__ */ jsxs("span", { className: "flex shrink-0 items-center gap-1.5", children: [
            riskLevel != null && /* @__PURE__ */ jsx(RiskBadge, { level: riskLevel, score: riskScore }),
            confidence != null && /* @__PURE__ */ jsxs("span", { className: "text-3xs text-muted-foreground", title: confidenceLabel === "Evidence" ? "Graph evidence confidence \u2014 not confidence in entity ownership" : confidenceLabel, children: [
              confidenceLabel,
              " ",
              Math.round(confidence * 100),
              "%"
            ] })
          ] }),
          value != null && /* @__PURE__ */ jsx(
            TruncatedText,
            {
              text: value,
              className: "min-w-0 truncate text-xs font-semibold tabular-nums"
            }
          )
        ] })
      ]
    }
  ) });
}
var VARIANT_STROKE = {
  transfer: "stroke-brand",
  approval: "stroke-muted-foreground",
  call: "stroke-finality-confirmed",
  inheritance: "stroke-finality-finalized",
  flow: "stroke-risk-medium",
  bridge: "stroke-deploy-blue",
  failed: "stroke-risk-critical",
  authored: "stroke-risk-medium",
  default: "stroke-muted-foreground"
};
function curvedPath(from, to, curvature) {
  if (!curvature) return `M ${from.x} ${from.y} L ${to.x} ${to.y}`;
  const mx = (from.x + to.x) / 2;
  const my = (from.y + to.y) / 2;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const nx = -dy;
  const ny = dx;
  const len = Math.hypot(nx, ny) || 1;
  const cx = mx + nx / len * curvature;
  const cy = my + ny / len * curvature;
  return `M ${from.x} ${from.y} Q ${cx} ${cy} ${to.x} ${to.y}`;
}
function GraphEdge({
  from,
  to,
  variant = "default",
  label,
  curvature = 0,
  directed = true,
  dashed = false,
  animated = false,
  selected = false,
  strokeWidth = 1.5,
  className,
  onClick
}) {
  const markerId = React4.useId();
  const stroke = VARIANT_STROKE[variant];
  const d = curvedPath(from, to, curvature);
  const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
  return /* @__PURE__ */ jsxs("g", { className: cn(stroke, onClick && "cursor-pointer", className), onClick, children: [
    directed && /* @__PURE__ */ jsx("defs", { children: /* @__PURE__ */ jsx(
      "marker",
      {
        id: markerId,
        viewBox: "0 0 10 10",
        refX: "9",
        refY: "5",
        markerWidth: "6",
        markerHeight: "6",
        orient: "auto-start-reverse",
        children: /* @__PURE__ */ jsx("path", { d: "M 0 0 L 10 5 L 0 10 z", className: "fill-current" })
      }
    ) }),
    /* @__PURE__ */ jsx(
      "path",
      {
        d,
        fill: "none",
        strokeWidth: selected ? strokeWidth + 1 : strokeWidth,
        strokeDasharray: dashed ? "4 3" : void 0,
        className: cn("stroke-current transition-[stroke-width]", animated && "animate-pulse", selected && "drop-shadow"),
        markerEnd: directed ? `url(#${markerId})` : void 0
      }
    ),
    label && /* @__PURE__ */ jsx(
      "text",
      {
        x: mid.x,
        y: mid.y - 4,
        textAnchor: "middle",
        className: "fill-current text-3xs font-medium",
        children: label
      }
    )
  ] });
}
function MemberField({
  id,
  heading,
  placeholder,
  members,
  draft,
  onDraftChange,
  busy,
  onAdd,
  onRemove,
  run
}) {
  if (!onAdd && !onRemove) return null;
  const submit = () => {
    const value = draft.trim();
    if (!value) return;
    void run(() => onAdd?.(value));
    onDraftChange("");
  };
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
    /* @__PURE__ */ jsx(Label, { htmlFor: id, className: "text-xs", children: heading }),
    members.length > 0 ? /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-1", children: members.map((member) => /* @__PURE__ */ jsx(
      Tag,
      {
        label: member,
        onRemove: onRemove ? () => void run(() => onRemove(member)) : void 0
      },
      member
    )) }) : null,
    onAdd ? /* @__PURE__ */ jsxs("div", { className: "flex gap-1", children: [
      /* @__PURE__ */ jsx(
        Input,
        {
          id,
          value: draft,
          onChange: (e) => onDraftChange(e.target.value),
          onKeyDown: (e) => {
            if (e.key === "Enter" && draft.trim()) {
              e.preventDefault();
              submit();
            }
          },
          placeholder,
          className: "h-8"
        }
      ),
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          size: "sm",
          variant: "secondary",
          disabled: busy || !draft.trim(),
          onClick: submit,
          children: "Add"
        }
      )
    ] }) : null
  ] });
}
function NodeAssignMenu({
  node,
  state,
  handlers,
  entityOptions,
  editor,
  onDone,
  className
}) {
  const [label, setLabel] = React4.useState(state?.label ?? "");
  const [tag, setTag] = React4.useState("");
  const [group, setGroup] = React4.useState("");
  const [entity, setEntity] = React4.useState(state?.entityLabel ?? state?.entityId ?? "");
  const [busy, setBusy] = React4.useState(false);
  const tags = state?.tags ?? [];
  const groups = state?.groups ?? [];
  const listId = React4.useId();
  const run = React4.useCallback(
    async (fn, opts = {}) => {
      if (!fn) return;
      setBusy(true);
      try {
        await fn();
        if (opts.close) onDone?.();
      } finally {
        setBusy(false);
      }
    },
    [onDone]
  );
  const resolveEntityId = (raw) => {
    const match = entityOptions?.find((o) => o.label === raw || o.id === raw);
    return match?.id ?? raw;
  };
  const headline = displayChainText(node.label);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-3 text-sm", editor ? "w-80" : "w-64", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
      /* @__PURE__ */ jsx("p", { className: "truncate font-semibold", title: headline.title, children: headline.text }),
      node.address ? /* @__PURE__ */ jsx("p", { className: "truncate text-xs text-muted-foreground", title: node.address, children: /* @__PURE__ */ jsx(AddressDisplay, { address: node.address, interactive: false }) }) : null
    ] }),
    editor,
    !editor && handlers.onSetLabel ? /* @__PURE__ */ jsx(Field, { label: "Label", labelClassName: "text-xs", children: /* @__PURE__ */ jsxs("div", { className: "flex gap-1", children: [
      /* @__PURE__ */ jsx(
        Input,
        {
          value: label,
          onChange: (e) => setLabel(e.target.value),
          placeholder: "Display label",
          className: "h-8"
        }
      ),
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          size: "sm",
          variant: "secondary",
          disabled: busy || !label.trim() || label.trim() === (state?.label ?? ""),
          onClick: () => run(() => handlers.onSetLabel?.(node, label.trim()), { close: true }),
          children: "Save"
        }
      )
    ] }) }) : null,
    !editor && /* @__PURE__ */ jsx(
      MemberField,
      {
        id: `${listId}-tag`,
        heading: "Tags",
        placeholder: "Add a tag\u2026",
        members: tags,
        draft: tag,
        onDraftChange: setTag,
        busy,
        onAdd: handlers.onAddTag ? (value) => handlers.onAddTag?.(node, value) : void 0,
        onRemove: handlers.onRemoveTag ? (value) => handlers.onRemoveTag?.(node, value) : void 0,
        run
      }
    ),
    !editor && /* @__PURE__ */ jsx(
      MemberField,
      {
        id: `${listId}-group`,
        heading: "Groups",
        placeholder: "Attach a group\u2026",
        members: groups,
        draft: group,
        onDraftChange: setGroup,
        busy,
        onAdd: handlers.onAddGroup ? (value) => handlers.onAddGroup?.(node, value) : void 0,
        onRemove: handlers.onRemoveGroup ? (value) => handlers.onRemoveGroup?.(node, value) : void 0,
        run
      }
    ),
    !editor && handlers.onAssignEntity ? /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
      /* @__PURE__ */ jsx(Label, { htmlFor: `${listId}-entity`, className: "text-xs", children: "Entity" }),
      /* @__PURE__ */ jsxs("div", { className: "flex gap-1", children: [
        /* @__PURE__ */ jsx(
          Input,
          {
            id: `${listId}-entity`,
            value: entity,
            list: entityOptions && entityOptions.length > 0 ? listId : void 0,
            onChange: (e) => setEntity(e.target.value),
            placeholder: "Assign to entity\u2026",
            className: "h-8"
          }
        ),
        entityOptions && entityOptions.length > 0 ? /* @__PURE__ */ jsx("datalist", { id: listId, children: entityOptions.map((o) => /* @__PURE__ */ jsx("option", { value: o.label }, o.id)) }) : null,
        /* @__PURE__ */ jsx(
          Button,
          {
            type: "button",
            size: "sm",
            variant: "secondary",
            disabled: busy || !entity.trim(),
            onClick: () => run(() => handlers.onAssignEntity?.(node, resolveEntityId(entity.trim())), { close: true }),
            children: "Assign"
          }
        )
      ] }),
      state?.entityId && handlers.onClearEntity ? /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          size: "sm",
          variant: "ghost",
          className: "h-7 justify-start px-0 text-xs text-muted-foreground",
          disabled: busy,
          onClick: () => run(() => handlers.onClearEntity?.(node), { close: true }),
          children: "Clear entity mapping"
        }
      ) : null
    ] }) : null
  ] });
}

export { GRAPH_DEFAULT_HEIGHT, GRAPH_EDGE_VARIANTS, GRAPH_EDGE_VARIANT_COLOR, GRAPH_EDGE_VARIANT_LABELS, GRAPH_EDGE_VARIANT_TOKEN, GRAPH_NODE_VARIANTS, GRAPH_NODE_VARIANT_COLOR, GRAPH_NODE_VARIANT_LABELS, GRAPH_NODE_VARIANT_TOKEN, GraphEdge, GraphFrame, GraphFrameMessage, GraphNode, NodeAssignMenu, graphEdgeColor, graphNodeColor, resolveTokenColor, useGraphHeight, useInGraphFrame };
//# sourceMappingURL=chunk-T6AKMSPV.js.map
//# sourceMappingURL=chunk-T6AKMSPV.js.map